import { useCallback } from 'react';

import { useGetShippingLabelQuery, usePrintLabelMutation } from '@api/shippingLabel';
import { toast } from 'sonner';
import { InstanceStatus } from 'src/contexts/RDConnectContext';

import { useRDInstance } from './useRdInstance';
import { usePrinters } from 'src/contexts/hooks/usePrinters';

export const usePrint = () => {
  const { myConnectedInstance, isLoading, refresh } = useRDInstance();

  const [printLabel, { error: printError, reset: resetPrintLabel }] = usePrintLabelMutation();

  const isInstanceOnline = !!myConnectedInstance?.id &&
    myConnectedInstance?.status === InstanceStatus.ONLINE;

  const {
    data: templateData,
    refetch,
    isLoading: isLoadingTemplateData,
  } = useGetShippingLabelQuery(undefined, { skip: !isInstanceOnline });

  const { getPrintersData } = usePrinters(myConnectedInstance?.id);
  const printersData = getPrintersData();

  const isPrinterAndTemplateSelected = !!printersData.defaultPrinter && !!templateData?.template;

  const isOffline = !printersData.defaultPrinter?.isActive;

  const print = useCallback(
    async (pieceId?: number) => {
      if (!isInstanceOnline || !printersData.defaultPrinter || !pieceId) return;
      try {
        const result = await printLabel({ id: pieceId, rdConnectInstanceId: myConnectedInstance.id });
        if ('data' in result) {
          if (result.data?.objectData.success) {
            toast.success('Print request sent');
          } else {
            toast.error(result.data?.objectData.message || 'Something wrong with printer');
          }
        }
      } catch (error) {
        toast.error('Error while printing');
      }
    },
    [printersData?.defaultPrinter, myConnectedInstance?.id, isInstanceOnline, printLabel],
  );

  const refreshPrinter = async () => {
    refetch();
    printersData.refetch();
  };

  const refreshInstannces = () => {
    refresh();
    resetPrintLabel();
    printersData.refetch();
  };

  return {
    isInstanceOnline,
    isPrinterAndTemplateSelected,
    isOffline: !!printError || isOffline,
    defaultPrinterName: printersData.defaultPrinter?.name,
    iCanPrint: isInstanceOnline && isPrinterAndTemplateSelected && !printError && !isOffline,
    print,
    isLoading: isLoading || isLoadingTemplateData || printersData.isLoading,
    refetchInstances: refreshInstannces,
    refreshPrinter,
  };
};
